import React, { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { Icon } from "@iconify/react";
import TextField from "../../../Components/TextField";
import Button from "../../../Components/Button";
import { Typography } from "../../../Components/Typography";
import { updatePassword } from "../../../Utils/apiServices";

const UpdatePassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [token, setToken] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    // Extract token from URL query parameter
    const accessToken = searchParams.get("accesstoken");
    if (accessToken) {
      setToken(accessToken);
    } else {
      setError("Invalid or missing reset token. Please request a new password reset link.");
    }
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess(false);
    setSubmitting(true);

    // Validation
    if (!token) {
      setError("Invalid or missing reset token. Please request a new password reset link.");
      setSubmitting(false);
      return;
    }

    if (!newPassword.trim()) {
      setError("Password is required");
      setSubmitting(false);
      return;
    }

    if (newPassword.length < 6) {
      setError("Password must be at least 6 characters long");
      setSubmitting(false);
      return;
    }

    if (newPassword !== confirmPassword) {
      setError("Passwords do not match");
      setSubmitting(false);
      return;
    }

    try {
      const response = await updatePassword({
        token: token,
        newPassword: newPassword,
      });
      
      if (response.success) {
        setSuccess(true);
        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate("/login");
        }, 3000);
      }
    } catch (err) {
      setError(err.error || "Failed to update password. Please try again or request a new reset link.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-primary flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-lg bg-white shadow-lg rounded-2xl border border-slate-200 p-8 flex flex-col gap-6">
        <div className="flex flex-col items-center gap-4 text-center">
          <div>
            <img src="/ign-logo.svg" alt="IgniteNext Logo" className="h-15 w-50" />
          </div>
          <div>
            <Typography as="h1" className="text-2xl font-bold text-primary-900">
              Update Password
            </Typography>
            <Typography as="p" className="text-sm text-tertiary-600 mt-1">
              Enter your new password below.
            </Typography>
          </div>
        </div>

        {success ? (
          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-600">
              <Icon icon="lucide:check-circle" className="h-4 w-4" />
              <span>
                Password updated successfully! Redirecting to login...
              </span>
            </div>
          </div>
        ) : (
          <form className="flex flex-col gap-4" onSubmit={handleSubmit}>
            <TextField
              label="New Password"
              name="newPassword"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Enter your new password"
              required
              autoFocus
              showPasswordToggle
            />
            <TextField
              label="Confirm Password"
              name="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm your new password"
              required
              showPasswordToggle
            />

            {error && (
              <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600">
                <Icon icon="lucide:alert-circle" className="h-4 w-4" />
                <span>{error}</span>
              </div>
            )}

            <Button type="submit" fullWidth disabled={submitting || !token}>
              {submitting ? "Updating..." : "Update Password"}
            </Button>
          </form>
        )}

        <div className="flex items-center justify-center text-sm text-tertiary-600">
          <span>
            Remember your password?{" "}
            <Link to="/login" className="text-brand-blue hover:underline">
              Sign in
            </Link>
          </span>
        </div>
      </div>
    </div>
  );
};

export default UpdatePassword;

